'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { ReadOnlyListing } from 'hotdesk_typescript';
import { useHorizontalWheelNav } from '@/hotdesk-ui/hooks/useHorizontalWheelNav';
import ListingCard from './ListingCard';
import styles from './CarouselSection.module.scss';
export interface CarouselSectionProps {
  title: string;
  listings: ReadOnlyListing[];
  isRTL?: boolean;
  variant?: 'workspace' | 'listing';
  loading?: boolean;
  showBookNow?: boolean;
  workspaceListingCount?: number;
  onWorkspacesClick?: () => void;
  source?: string;
  membershipPassDiscountValue?: number;
  /** `minimal` renders the borderless landing card, six-up on desktop. */
  cardLayout?: 'grid' | 'minimal';
  /** Number of leading cards whose photo loads at high priority (LCP). */
  priorityCount?: number;
}
const MOBILE_BREAKPOINT = 640;
const responsive = {
  desktop: {
    breakpoint: {
      max: 3000,
      min: 1024
    },
    items: 3.5,
    slidesToSlide: 1
  },
  tablet: {
    breakpoint: {
      max: 1024,
      min: MOBILE_BREAKPOINT
    },
    items: 2.2,
    slidesToSlide: 1
  },
  mobile: {
    breakpoint: {
      max: MOBILE_BREAKPOINT,
      min: 0
    },
    items: 1.15,
    slidesToSlide: 1
  }
};

// The minimal card is roughly half the width of the grid card, so it packs
// six across the 1280px container (matching the design) and steps down from
// there rather than reusing the grid breakpoints.
const minimalResponsive = {
  desktop: {
    breakpoint: {
      max: 3000,
      min: 1280
    },
    items: 6,
    slidesToSlide: 1
  },
  laptop: {
    breakpoint: {
      max: 1280,
      min: 1024
    },
    items: 5,
    slidesToSlide: 1
  },
  tablet: {
    breakpoint: {
      max: 1024,
      min: MOBILE_BREAKPOINT
    },
    items: 3.2,
    slidesToSlide: 1
  },
  mobile: {
    breakpoint: {
      max: MOBILE_BREAKPOINT,
      min: 0
    },
    items: 2.2,
    slidesToSlide: 1
  }
};
const SkeletonCard: FC = () => <div className={styles.skeletonCard} data-sentry-component="SkeletonCard" data-sentry-source-file="CarouselSection.tsx">
        <div className={styles.skeletonImage} />
        <div className={styles.skeletonBody}>
            <div className={styles.skeletonLine} style={{
      width: '70%'
    }} />
            <div className={styles.skeletonLine} style={{
      width: '50%'
    }} />
            <div className={styles.skeletonLine} style={{
      width: '40%'
    }} />
        </div>
    </div>;
const CarouselSection: FC<CarouselSectionProps> = ({
  title,
  listings,
  isRTL = false,
  variant = 'workspace',
  loading = false,
  showBookNow = false,
  workspaceListingCount,
  onWorkspacesClick,
  source,
  membershipPassDiscountValue,
  cardLayout = 'grid',
  priorityCount = 0
}) => {
  const t = useTranslations('landing-v4.card');
  const carouselRef = useRef<Carousel | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const isMinimal = cardLayout === 'minimal';
  const activeResponsive = isMinimal ? minimalResponsive : responsive;
  const sectionClass = `${styles.section} ${isRTL ? styles.rtl : ''} ${isMinimal ? styles.sectionMinimal : ''}`;
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Desktop keeps the original RMC arrows. Mobile uses native scroll.
  const handlePrev = () => {
    if (isMobile) {
      const el = scrollRef.current;
      if (!el) return;
      const slide = el.querySelector(`.${styles.scrollSlide}`) as HTMLElement | null;
      const delta = slide?.offsetWidth ?? Math.round(el.clientWidth * 0.8);
      el.scrollBy({
        left: isRTL ? delta : -delta,
        behavior: 'smooth'
      });
      return;
    }
    if (carouselRef.current && carouselRef.current.state.currentSlide > 0) {
      carouselRef.current.goToSlide(carouselRef.current.state.currentSlide - 1);
    }
  };
  const handleNext = () => {
    if (isMobile) {
      const el = scrollRef.current;
      if (!el) return;
      const slide = el.querySelector(`.${styles.scrollSlide}`) as HTMLElement | null;
      const delta = slide?.offsetWidth ?? Math.round(el.clientWidth * 0.8);
      el.scrollBy({
        left: isRTL ? -delta : delta,
        behavior: 'smooth'
      });
      return;
    }
    const carousel = carouselRef.current;
    if (!carousel) return;
    const {
      currentSlide,
      totalItems,
      slidesToShow
    } = carousel.state;
    const isLastSlide = currentSlide > totalItems - slidesToShow - 1;
    const landOnSlide = totalItems > 4 ? 0 : 1;
    if (isLastSlide) {
      carousel.goToSlide(landOnSlide);
    } else {
      carousel.goToSlide(carousel.state.currentSlide + 1);
    }
  };
  const carouselKey = useMemo(() => listings.map(l => l.id).join(','), [listings]);

  // Read by the wheel hook below, so it has to be worked out before the
  // early returns rather than beside the markup that uses it.
  const maxVisibleItems = Math.ceil(activeResponsive.desktop.items);
  const shouldLoop = listings.length > maxVisibleItems - 1;

  // Sideways trackpad swipes and Shift + wheel step the row, the same as the
  // arrows. Mobile renders a real scroller, which the browser already
  // handles, so this is the desktop track only.
  const wheelRef = useHorizontalWheelNav<HTMLDivElement>({
    onPrev: handlePrev,
    onNext: handleNext,
    enabled: !isMobile && shouldLoop,
    invert: isRTL
  });
  if (!loading && (!listings || listings.length === 0)) return null;
  if (loading) {
    return <section className={sectionClass}>
                <div className={styles.header}>
                    {title ? <h2 className={styles.title}>{title}</h2> : <div className={styles.skeletonTitle} aria-hidden="true" />}
                </div>
                <div className={styles.skeletonGrid}>
                    {(isMinimal ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4]).map(i => <SkeletonCard key={i} />)}
                </div>
            </section>;
  }
  const showSeeAll = variant === 'listing' && !!workspaceListingCount && workspaceListingCount > 11 && shouldLoop;
  const showViewMore = variant !== 'listing' && source !== 'memberships' && shouldLoop;
  return <section className={sectionClass} data-sentry-component="CarouselSection" data-sentry-source-file="CarouselSection.tsx">
            <div className={styles.header} style={{
      justifyContent: source !== 'memberships' ? 'space-between' : 'end'
    }}>
                {source !== 'memberships' && <h2 className={styles.title}>{title}</h2>}
                {shouldLoop && <div className={styles.navButtons}>
                        <button className={styles.navBtn} onClick={handlePrev} aria-label="Previous" type="button">
                            <ChevronLeft size={20} />
                        </button>
                        <button className={styles.navBtn} onClick={handleNext} aria-label="Next" type="button">
                            <ChevronRight size={20} />
                        </button>
                    </div>}
            </div>

            {/* Desktop/tablet: original RMC — zero visual change. */}
            {!isMobile && <div ref={wheelRef} className={styles.carouselContainer}>
                    <Carousel key={`${carouselKey}-${cardLayout}`} ref={carouselRef} responsive={activeResponsive} ssr={false} rtl={isRTL} arrows={false} infinite={false} centerMode={false}>
                        {listings.map((listing, idx) => <ListingCard key={listing.id || idx} listing={listing} variant={variant} layout={cardLayout} priority={idx < priorityCount} showBookNow={showBookNow} source={source} membershipPassDiscountValue={membershipPassDiscountValue} />)}
                        {showSeeAll && <Link href="#" onClick={onWorkspacesClick} className={styles.viewMoreCard}>
                                <div className={styles.viewMoreInner}>
                                    <span className={styles.viewMoreIcon}>
                                        <ArrowRight size={24} />
                                    </span>
                                    <span className={styles.viewMoreText}>
                                        {t('seeAll')}
                                    </span>
                                </div>
                            </Link>}
                        {showViewMore && <Link href="/workspaces" className={styles.viewMoreCard}>
                                <div className={styles.viewMoreInner}>
                                    <span className={styles.viewMoreIcon}>
                                        <ArrowRight size={24} />
                                    </span>
                                    <span className={styles.viewMoreText}>
                                        {t('viewMore')}
                                    </span>
                                </div>
                            </Link>}
                    </Carousel>
                </div>}

            {/* Mobile only: native scroll for snappier feel + flush View more. */}
            {isMobile && <div ref={scrollRef} className={styles.scrollViewport} dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className={styles.scrollTrack}>
                        {listings.map((listing, idx) => <div key={listing.id || idx} className={styles.scrollSlide}>
                                <ListingCard listing={listing} variant={variant} layout={cardLayout} priority={idx < priorityCount} showBookNow={showBookNow} source={source} membershipPassDiscountValue={membershipPassDiscountValue} />
                            </div>)}
                        {showSeeAll && <div className={styles.scrollSlide}>
                                <Link href="#" onClick={onWorkspacesClick} className={styles.viewMoreCard}>
                                    <div className={styles.viewMoreInner}>
                                        <span className={styles.viewMoreIcon}>
                                            <ArrowRight size={24} />
                                        </span>
                                        <span className={styles.viewMoreText}>
                                            {t('seeAll')}
                                        </span>
                                    </div>
                                </Link>
                            </div>}
                        {showViewMore && <div className={styles.scrollSlide}>
                                <Link href="/workspaces" className={styles.viewMoreCard}>
                                    <div className={styles.viewMoreInner}>
                                        <span className={styles.viewMoreIcon}>
                                            <ArrowRight size={24} />
                                        </span>
                                        <span className={styles.viewMoreText}>
                                            {t('viewMore')}
                                        </span>
                                    </div>
                                </Link>
                            </div>}
                    </div>
                </div>}
        </section>;
};
export default CarouselSection;